/** Shared between the Worker and the browser app. */

export const TIME_ZONE = "America/New_York";

/** Order (Peter): tours first, then everything else, each group A to Z. */
export const INTERESTS = [
  { key: "ecls", label: "EC/LS Tours", sa: false },
  { key: "ms", label: "MS Tours", sa: true },
  { key: "us", label: "US Tours", sa: true },
  { key: "coffee", label: "Coffee & Conversation", sa: false },
  { key: "notes", label: "Note Card Completion", sa: true },
] as const;
export type InterestKey = (typeof INTERESTS)[number]["key"];

export const TITLES = {
  pa: ["Early Childhood Captain", "Lower School Captain", "Middle School Captain", "Upper School Captain"],
  sa: ["Club President", "9th Grade Captain", "10th Grade Captain", "11th Grade Captain", "12th Grade Captain"],
} as const;

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const INACTIVE_MESSAGE = "Your profile is not active, please contact the Admissions Team.";

export const CODE_TTL_MS = 15 * 60 * 1000;

/** The school year runs Jul 1 to Jun 30. Returns the starting calendar year, e.g. 2026 for "2026-2027". */
export function schoolYearStart(d: Date, tz = TIME_ZONE): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "numeric" }).formatToParts(d);
  const y = Number(parts.find((p) => p.type === "year")!.value);
  const m = Number(parts.find((p) => p.type === "month")!.value);
  return m >= 7 ? y : y - 1;
}

/**
 * Every sign-in ends when the school year does: midnight at the end of June 30, school time (Peter, 2026-11-04).
 * Everyone signs in fresh each July, and ambassadors who phased out are signed out on their own.
 * July 1 is always daylight time in Miami (UTC-4), so midnight there is 04:00 UTC.
 */
export function sessionEnd(now = Date.now()) {
  return Date.UTC(schoolYearStart(new Date(now)) + 1, 6, 1, 4);
}

export function schoolYearLabel(start: number) {
  return `${start}-${start + 1} School Year`;
}

/** Formats 10 US digits as (xxx) xxx-xxxx. Returns null when it isn't a 10-digit number. */
export function formatPhone(raw: string): string | null {
  let d = raw.replace(/\D/g, "");
  if (d.length === 11 && d[0] === "1") d = d.slice(1);
  if (d.length !== 10) return null;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Event types. The type sets the color everywhere (softened from the Google Sheet's colors). */
export const EVENT_TYPES = [
  { key: "team", label: "Ambassador Team Event", color: "#B4A7D6" },
  { key: "coffee", label: "Coffee & Conversation", color: "#FFB65C" },
  { key: "ecls", label: "EC/LS Tour", color: "#8EE68E" },
  { key: "ms", label: "MS Tour", color: "#FFEE70" },
  { key: "notes", label: "Note Cards", color: "#F5B3C2" },
  { key: "office", label: "Office Work", color: "#C5CCD8" },
  { key: "openhouse", label: "Open Houses", color: "#F08CF0" },
  { key: "special", label: "Special Events", color: "#9DB8FF" },
  { key: "us", label: "US Tour", color: "#7EEAEA" },
] as const;
export type EventType = (typeof EVENT_TYPES)[number]["key"];
export const typeInfo = (k: string) => EVENT_TYPES.find((t) => t.key === k) ?? EVENT_TYPES[7];

/** Which profile interest an event type belongs to (for last-minute alerts). */
export const TYPE_INTEREST: Record<string, string | undefined> = { coffee: "coffee", ecls: "ecls", ms: "ms", notes: "notes", us: "us" };

export const DURATIONS = [5, 10, 15, 20, 30, 45, 60, 75, 90, 120, 150, 180];

/** Wording rule (Peter): "Tour Lead" only when the event name contains "tour". */
export const isTour = (title: string) => /tour/i.test(title);

/** "9:30 AM" from "09:30". */
export function fmtTime(t: string) {
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

/** Days are "YYYY-MM-DD" strings in school time. */
export function parseDay(s: string) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}
export function addDays(s: string, n: number) {
  const d = parseDay(s);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
/** The next school day after `s`: tomorrow, or Monday when tomorrow is a weekend. */
export function nextWorkday(s: string) {
  let d = addDays(s, 1);
  while ([0, 6].includes(parseDay(d).getUTCDay())) d = addDays(d, 1);
  return d;
}
export const fmtDay = (s: string, o: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" }) =>
  parseDay(s).toLocaleDateString("en-US", { ...o, timeZone: "UTC" });

/** Today and the time now in school time. */
export function nowLocal(d = new Date()) {
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE }).format(d);
  const time = new Intl.DateTimeFormat("en-GB", { timeZone: TIME_ZONE, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(d);
  return { day, time };
}

/**
 * Names allow letters (accents included), spaces, hyphens, apostrophes and periods.
 * No emojis, digits or symbols (Peter, 2026-11-04). Returns the cleaned name, or null when it isn't allowed.
 */
export function cleanName(v: unknown): string | null {
  const s = String(v ?? "").normalize("NFC").replace(/[‘’ʼ]/g, "'").replace(/\s+/g, " ").trim();
  if (!/^[\p{Script=Latin}\p{M}' .-]*$/u.test(s)) return null;
  if (s && !/\p{Script=Latin}/u.test(s)) return null;
  return s;
}

/**
 * Season cards (Peter, 2026-11-04): a kick-off card at the start of each Admissions Season and a thank-you
 * at the end. Admins turn each on or off and edit the words. {first} becomes the person's first name and
 * {year} the school year, e.g. 2027-2028. Both start off; the site launched mid-way through 2026-2027, so
 * the kick-off stays off this year.
 */
export const SEASON_CARD_DEFAULTS = {
  kickoff: {
    on: false,
    title: "Welcome to the {year} Admissions Season!",
    message:
      "Thank you for being a Spartan Ambassador, {first}. So many families meet MCDS through you, and we can't wait to get started.\n\nTours and events are on the calendar now. Sign up for the ones that fit your schedule.\n\nThe Admissions Team",
    button: "Let's Go, Spartans!",
  },
  thanks: {
    on: false,
    title: "Thank You, {first}!",
    message:
      "From all of us in Admissions: thank you for sharing your love of MCDS with so many families this year. You made a real difference.\n\nThe Admissions Team",
    button: "Close",
  },
};
/** The thank-you card shows "You helped with N events" only from this many points. */
export const THANKS_COUNT_FROM = 5;
export const SEASON_CARD_LIMITS = { title: 80, message: 800, button: 30 };
export const fillCard = (s: string, first: string, sy: number) => s.replace(/\{first\}/g, first).replace(/\{year\}/g, `${sy}-${sy + 1}`);

/** UTC milliseconds for a school-time day and "HH:MM" (handles daylight saving). */
export function schoolTimeToUtc(day: string, time: string, tz = TIME_ZONE) {
  const [y, mo, d] = day.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const offsetAt = (ms: number) => {
    const p = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric" }).formatToParts(new Date(ms));
    const g = (t: string) => Number(p.find((x) => x.type === t)!.value);
    return Date.UTC(g("year"), g("month") - 1, g("day"), g("hour"), g("minute")) - ms;
  };
  let t = guess - offsetAt(guess);
  t = guess - offsetAt(t);
  return t;
}
